import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContainer, Flex, ProgressCircle, ToastQueue, View } from '@adobe/react-spectrum';
import { useTranslation } from 'react-i18next';
import { useDependency } from '../../../../contexts/DependencyProvider';
import { ListDocumentCategoryRequest } from '../../../../services/soap/document_library/requests/ListDocumentCategoryRequest';
import DocumentUpload from '../../../../components/protected/layout-components/DocumentComponent/DocumentUpload';
import { UpdateInvoiceRequest } from '../../../../services/soap/invoice/requests/UpdateInvoiceRequest';
import { buildInvoicePayloadForSave } from '../../../../shared/invoice/transactionBuilder';

interface IInvoiceDocumentUploadDialogProps {
  isOpen: boolean;
  invoice: any;
  onClose: () => void;
  onUploaded: () => Promise<void> | void;
}

const buildCategoryTree = (data: any[]) => {
  const map: Record<string, any> = {};
  const roots: any[] = [];

  data.forEach((item: any) => {
    map[item.id] = { ...item, subChild: [] };
  });

  data.forEach((item: any) => {
    if (item.parent && map[item.parent]) {
      map[item.parent].subChild.push(map[item.id]);
    } else if (map[item.id]) {
      roots.push(map[item.id]);
    }
  });

  return roots;
};

const getFirstCategoryId = (items: any[]): string => {
  if (!items.length) return '';
  const first = items[0];
  if (!first) return '';
  if (first.id) return String(first.id);
  if (Array.isArray(first.subChild) && first.subChild.length > 0) {
    return getFirstCategoryId(first.subChild);
  }
  return '';
};

const normalizeInvoiceDocuments = (documentValue: any): Array<{ documentId: string; name: string }> => {
  if (!documentValue) return [];
  const documents = Array.isArray(documentValue) ? documentValue : [documentValue];
  return documents
    .map((item: any) => ({
      documentId: String(item?.documentId || item?.id || ''),
      name: String(item?.name || item?.fileName || ''),
    }))
    .filter((item) => item.documentId);
};

const InvoiceDocumentUploadDialog: React.FC<IInvoiceDocumentUploadDialogProps> = ({
  isOpen,
  invoice,
  onClose,
  onUploaded,
}) => {
  const { documentLibraryService, invoiceService, store } = useDependency();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [categoryTree, setCategoryTree] = useState<any[]>([]);

  useEffect(() => {
    let isActive = true;

    const loadCategories = async () => {
      if (!isOpen) return;
      setIsLoading(true);
      try {
        const response: any = await documentLibraryService.listDocumentCategory(
          new ListDocumentCategoryRequest(store.Server, store.SessionId)
        );
        const categories = Array.isArray(response?.item)
          ? response.item
          : Array.isArray(response)
            ? response
            : [];

        if (isActive) {
          setCategoryTree(buildCategoryTree(categories));
        }
      } catch {
        if (isActive) {
          setCategoryTree([]);
        }
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    loadCategories();
    return () => {
      isActive = false;
    };
  }, [documentLibraryService, isOpen, store.Server, store.SessionId]);

  const defaultCategoryId = useMemo(() => getFirstCategoryId(categoryTree), [categoryTree]);

  const invoiceTreeHandle = useMemo(() => {
    const activityId = String(invoice?.activity || '').trim();
    if (activityId && activityId !== '0') return activityId;

    const projectId = String(invoice?.project || '').trim();
    if (projectId && projectId !== '0') return projectId;

    const folderId = String(invoice?.folder || '').trim();
    if (folderId && folderId !== '0') return folderId;

    const userContactTreeHandle = String(store?.session?.organization?.USERCONTACT?.treeHandle || '').trim();
    if (userContactTreeHandle && userContactTreeHandle !== '0') return userContactTreeHandle;

    return '';
  }, [invoice?.activity, invoice?.folder, invoice?.project, store?.session?.organization?.USERCONTACT?.treeHandle]);

  const selectedItem = useMemo(
    () => ({
      id: invoiceTreeHandle,
    }),
    [invoiceTreeHandle]
  );

  const handleUploadedDocuments = async (documents: Array<{ documentId: string; name: string }>) => {
    const existingDocuments = normalizeInvoiceDocuments(invoice?.DOCUMENT);
    const mergedDocuments = [...existingDocuments];

    documents.forEach((document) => {
      const existingIndex = mergedDocuments.findIndex((item) => String(item.documentId) === String(document.documentId));
      if (existingIndex >= 0) {
        mergedDocuments[existingIndex] = {
          documentId: String(document.documentId),
          name: document.name,
        };
      } else {
        mergedDocuments.push({
          documentId: String(document.documentId),
          name: document.name,
        });
      }
    });

    const basePayload = {
      ...invoice,
      id: String(invoice?.id || ''),
      DOCUMENT: mergedDocuments,
    };

    const { payload, errors } = buildInvoicePayloadForSave(basePayload, { isCreate: false });

    if (errors.length > 0) {
      throw new Error(errors[0]);
    }

    const response: any = await invoiceService.updateInvoice(
      new UpdateInvoiceRequest(store.Server, store.SessionId, payload)
    );

    if (response?.result !== 'OK') {
      throw new Error(
          response?.EXCEPTION?.message ||
          response?.EXCEPTION?.reason ||
          t('invoice_messages.error_occurred', {
            ns: 'invoice',
            defaultValue: 'Unable to link uploaded document.',
          })
      );
    }

    await onUploaded();
    ToastQueue.positive(
      t('document_uploaded_successfully', {
        ns: 'layout_components',
        defaultValue: 'Document uploaded successfully.',
      }),
      { timeout: 2000 }
    );
  };

  return (
    <DialogContainer onDismiss={onClose}>
      {isOpen ? (
        isLoading ? (
          <Dialog>
            <Flex width="100%" justifyContent="center" marginTop={10}>
              <ProgressCircle
                aria-label={t('loading_document_categories', {
                  ns: 'layout_components',
                  defaultValue: 'Loading document categories',
                })}
                isIndeterminate
              />
            </Flex>
          </Dialog>
        ) : !invoiceTreeHandle ? (
          <Dialog>
            <View padding="size-200">
              {t('invoice_upload_unavailable', {
                ns: 'layout_components',
                defaultValue:
                  'Upload is unavailable because no valid upload node is available for this invoice.',
              })}
            </View>
          </Dialog>
        ) : (
          <View>
            <DocumentUpload
              categoryId={defaultCategoryId}
              documentCategoryData={categoryTree}
              getDocumentsData={onUploaded}
              close={onClose}
              selectedItem={selectedItem as any}
              contextIdOverride={String(invoice?.id || '')}
              onUploadedDocuments={handleUploadedDocuments}
            />
          </View>
        )
      ) : null}
    </DialogContainer>
  );
};

export default InvoiceDocumentUploadDialog;
